import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { CrossReferenceCellKey, CrossReferenceState } from 'components/CrossReferenceValueViewer/types';
import { stateToWireEntry } from 'components/CrossReferenceValueViewer/utils/serialize';
import type { CrossReferenceStore, WireByCellKey } from './types';

const buildInitialStateForConfig = (config: {
  rowMode: CrossReferenceState['rowMode'];
  columns: CrossReferenceState['columnsConfig'];
}): CrossReferenceState => {
  const rows: CrossReferenceState['rows'] = [];
  if (config.rowMode === 'multi_row') {
    rows.push({ position: 0, value: null, metadata: {}, isPlaceholder: true });
  } else {
    rows.push({ position: 0, value: null, metadata: {}, isPlaceholder: false });
  }
  return {
    rowMode: config.rowMode,
    columnsConfig: config.columns,
    rows,
    isDirty: false,
  };
};

const useCrossReferenceStore = create<CrossReferenceStore>()(
  devtools(
    (set, get) => ({
      states: {},

      hydrate: statesByCellKey => set(() => ({ states: { ...statesByCellKey } }), false, 'hydrate'),

      seedIfMissing: (cellKey, config) =>
        set(
          state => {
            if (state.states[cellKey]) {
              return state;
            }
            return {
              states: {
                ...state.states,
                [cellKey]: buildInitialStateForConfig(config),
              },
            };
          },
          false,
          'seedIfMissing'
        ),

      setRows: (cellKey, rows) =>
        set(
          state => {
            const existing = state.states[cellKey];
            if (!existing) {
              return state;
            }
            return {
              states: {
                ...state.states,
                [cellKey]: {
                  ...existing,
                  rows,
                  isDirty: true,
                },
              },
            };
          },
          false,
          'setRows'
        ),

      clearDirty: cellKeyPrefix =>
        set(
          state => ({
            states: Object.fromEntries(
              Object.entries(state.states).map(([key, value]) => {
                if (cellKeyPrefix && !key.startsWith(cellKeyPrefix)) {
                  return [key, value];
                }
                return [key, { ...value, isDirty: false }];
              })
            ),
          }),
          false,
          'clearDirty'
        ),

      isAnyDirty: () => Object.values(get().states).some(s => s.isDirty),

      collectDirtyPayload: () => {
        const out: WireByCellKey = {};
        Object.entries(get().states).forEach(([cellKey, state]) => {
          if (!state.isDirty) {
            return;
          }
          out[cellKey as CrossReferenceCellKey] = stateToWireEntry(state);
        });
        return out;
      },
    }),
    { name: 'CrossReferenceStore' }
  )
);

export default useCrossReferenceStore;
